import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import UserService from '../../services/userService'
import { User } from '../../types/user'

// Async thunks
export const fetchCurrentUser = createAsyncThunk(
  'user/fetchCurrentUser',
  async (_, { rejectWithValue }) => {
    try {
      const response = await UserService.getCurrentUser()
      if (response.success && response.data) {
        return response.data
      } else {
        return rejectWithValue(response.error || 'Failed to fetch user')
      }
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error')
    }
  },
)

// NOTE: fetchUserProfile now returns User type (same as fetchCurrentUser)
// Both use the same /user endpoint - getUserProfile internally calls getCurrentUser
export const fetchUserProfile = createAsyncThunk(
  'user/fetchUserProfile',
  async (_, { rejectWithValue }) => {
    try {
      const response = await UserService.getUserProfile()
      if (response.success && response.data) {
        return response.data
      } else {
        return rejectWithValue(response.error || 'Failed to fetch user profile')
      }
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error')
    }
  },
)

export const updateUserProfile = createAsyncThunk(
  'user/updateUserProfile',
  async (
    profileData: {
      personal?: {
        firstName?: string
        lastName?: string
        birthDate?: string
      }
      contacts?: {
        phone?: string
        email?: string
      }
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await UserService.updateUserProfile(profileData)
      if (response.success && response.data) {
        return response.data
      } else {
        return rejectWithValue(response.error || 'Failed to update user profile')
      }
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error')
    }
  },
)

export const fetchUserOrders = createAsyncThunk(
  'user/fetchUserOrders',
  async (
    params: {
      limit?: number
      offset?: number
      status?: string
    } = {},
    { rejectWithValue },
  ) => {
    try {
      const response = await UserService.getUserOrders(params)
      if (response.success && response.data) {
        return response.data
      } else {
        return rejectWithValue(response.error || 'Failed to fetch user orders')
      }
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error')
    }
  },
)

export const fetchOrderByNumber = createAsyncThunk(
  'user/fetchOrderByNumber',
  async (orderNumber: string, { rejectWithValue }) => {
    try {
      const response = await UserService.getOrderByNumber(orderNumber)
      if (response.success && response.data) {
        return response.data
      } else {
        return rejectWithValue(response.error || 'Failed to fetch order')
      }
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error')
    }
  },
)

// User state interface
interface UserState {
  // User data
  user: User | null
  userProfile: User | null  // Now uses User type (same as user field)

  // Orders data
  orders: Array<{
    id: string
    orderNumber: string
    customerId: string
    phone: string | null
    email: string
    firstName: string
    lastName: string
    status: string
    statusLabel: string
    deliveryType: string
    deliverySubtype: string | null
    redirectUrl: string | null
    amount: number
    discountAmount: number
    grandTotalAmount: number
    productDiscountAmount?: number
    shippingAddress: {
      id: string
      isPrimary: boolean | null
      region: string
      deliveryType: string | null
      deliverySubtype: string | null
      shippingCity: string
      warehouseId: string | null
      warehouseLabel: string | null
      shippingStreet: string
      shippingHouse: string | null
      shippingEntrance: string | null
      shippingApartment: string | null
      shippingComment: string | null
      createdAt: string
      updatedAt: string
    }
    notesPrivate: string
    createdAt: string
    updatedAt: string
    items: Array<{
      id: string
      productId: string
      title: string
      icon: string
      unitPrice: number
      quantity: number
      totalAmount: number
      basePrice?: number
      discountPercent?: number
      discountPerUnit?: number
      discountAmount?: number
      createdAt: string
      updatedAt: string
    }>
    events: Array<{
      status: string
      statusLabel: string
      message: string
      changedAt: string
    }>
  }> | null
  currentOrder: {
    id: string
    orderNumber: string
    status: string
    totalAmount: number
    currency: string
    createdAt: string
    updatedAt: string
    items: Array<{
      id: string
      name: string
      description: string
      price: number
      currency: string
      quantity: number
      image: string
    }>
    shippingAddress: {
      id: string
      recipientName: string
      phone: string
      city: string
      street: string
      house: string
      apartment?: string
      entrance?: string
      comment?: string
    }
    paymentMethod: string
    deliveryMethod: string
  } | null

  // Loading states
  isLoading: boolean
  isProfileLoading: boolean
  isUpdating: boolean
  isOrdersLoading: boolean
  isOrderLoading: boolean

  // Error states
  error: string | null
  profileError: string | null
  updateError: string | null
  ordersError: string | null
  orderError: string | null

  // Last fetch timestamps
  lastUserFetch: number | null
  lastProfileFetch: number | null
  lastOrdersFetch: number | null

  // Cache validity (in milliseconds)
  cacheValidity: number
}

const initialState: UserState = {
  user: null,
  userProfile: null,
  orders: null,
  currentOrder: null,
  isLoading: false,
  isProfileLoading: false,
  isUpdating: false,
  isOrdersLoading: false,
  isOrderLoading: false,
  error: null,
  profileError: null,
  updateError: null,
  ordersError: null,
  orderError: null,
  lastUserFetch: null,
  lastProfileFetch: null,
  lastOrdersFetch: null,
  cacheValidity: 5 * 60 * 1000, // 5 minutes
}

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    // Clear all user data
    clearUserData: state => {
      state.user = null
      state.userProfile = null
      state.orders = null
      state.currentOrder = null
      state.error = null
      state.profileError = null
      state.updateError = null
      state.ordersError = null
      state.orderError = null
      state.lastUserFetch = null
      state.lastProfileFetch = null
      state.lastOrdersFetch = null
    },

    // Clear errors
    clearErrors: state => {
      state.error = null
      state.profileError = null
      state.updateError = null
      state.ordersError = null
      state.orderError = null
    },

    // Set user data manually (for cases when we have data from other sources)
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload
      state.lastUserFetch = Date.now()
    },

    // Set user profile manually
    setUserProfile: (state, action: PayloadAction<User>) => {
      state.userProfile = action.payload
      state.lastProfileFetch = Date.now()
    },

    // Update cache validity
    setCacheValidity: (state, action: PayloadAction<number>) => {
      state.cacheValidity = action.payload
    },
  },
  extraReducers: builder => {
    // Fetch current user
    builder
      .addCase(fetchCurrentUser.pending, state => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchCurrentUser.fulfilled, (state, action) => {
        state.isLoading = false
        state.user = action.payload
        state.lastUserFetch = Date.now()
        state.error = null
      })
      .addCase(fetchCurrentUser.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })

    // Fetch user profile
    builder
      .addCase(fetchUserProfile.pending, state => {
        state.isProfileLoading = true
        state.profileError = null
      })
      .addCase(fetchUserProfile.fulfilled, (state, action) => {
        state.isProfileLoading = false
        state.userProfile = action.payload
        state.lastProfileFetch = Date.now()
        state.profileError = null
      })
      .addCase(fetchUserProfile.rejected, (state, action) => {
        state.isProfileLoading = false
        state.profileError = action.payload as string
      })

    // Update user profile
    builder
      .addCase(updateUserProfile.pending, state => {
        state.isUpdating = true
        state.updateError = null
      })
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        state.isUpdating = false
        state.user = action.payload
        state.lastUserFetch = Date.now()
        state.updateError = null
      })
      .addCase(updateUserProfile.rejected, (state, action) => {
        state.isUpdating = false
        state.updateError = action.payload as string
      })

    // Fetch user orders
    builder
      .addCase(fetchUserOrders.pending, state => {
        state.isOrdersLoading = true
        state.ordersError = null
      })
      .addCase(fetchUserOrders.fulfilled, (state, action) => {
        state.isOrdersLoading = false
        state.orders = action.payload // API now returns array directly
        state.lastOrdersFetch = Date.now()
        state.ordersError = null
      })
      .addCase(fetchUserOrders.rejected, (state, action) => {
        state.isOrdersLoading = false
        state.ordersError = action.payload as string
      })

    // Fetch order by number
    builder
      .addCase(fetchOrderByNumber.pending, state => {
        state.isOrderLoading = true
        state.orderError = null
      })
      .addCase(fetchOrderByNumber.fulfilled, (state, action) => {
        state.isOrderLoading = false
        state.currentOrder = action.payload
        state.orderError = null
      })
      .addCase(fetchOrderByNumber.rejected, (state, action) => {
        state.isOrderLoading = false
        state.orderError = action.payload as string
      })
  },
})

// Selectors
export const selectUser = (state: { user: UserState }) => state.user.user
export const selectUserProfile = (state: { user: UserState }) => state.user.userProfile
export const selectUserLoading = (state: { user: UserState }) => state.user.isLoading
export const selectUserProfileLoading = (state: { user: UserState }) => state.user.isProfileLoading
export const selectUserUpdating = (state: { user: UserState }) => state.user.isUpdating
export const selectUserError = (state: { user: UserState }) => state.user.error
export const selectUserProfileError = (state: { user: UserState }) => state.user.profileError
export const selectUserUpdateError = (state: { user: UserState }) => state.user.updateError

// Helper selectors
export const selectIsUserDataStale = (state: { user: UserState }) => {
  const { lastUserFetch, cacheValidity } = state.user
  if (!lastUserFetch) return true
  return Date.now() - lastUserFetch > cacheValidity
}

export const selectIsProfileDataStale = (state: { user: UserState }) => {
  const { lastProfileFetch, cacheValidity } = state.user
  if (!lastProfileFetch) return true
  return Date.now() - lastProfileFetch > cacheValidity
}

export const selectUserIsAuthenticated = (state: { user: UserState }) => {
  return !!state.user.user
}

// Orders selectors
export const selectUserOrders = (state: { user: UserState }) => state.user.orders
export const selectCurrentOrder = (state: { user: UserState }) => state.user.currentOrder
export const selectOrdersLoading = (state: { user: UserState }) => state.user.isOrdersLoading
export const selectOrderLoading = (state: { user: UserState }) => state.user.isOrderLoading
export const selectOrdersError = (state: { user: UserState }) => state.user.ordersError
export const selectOrderError = (state: { user: UserState }) => state.user.orderError

export const selectIsOrdersDataStale = (state: { user: UserState }) => {
  const { lastOrdersFetch, cacheValidity } = state.user
  if (!lastOrdersFetch) return true
  return Date.now() - lastOrdersFetch > cacheValidity
}

// Actions
export const { clearUserData, clearErrors, setUser, setUserProfile, setCacheValidity } =
  userSlice.actions

export default userSlice.reducer
